import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowRight,
  Check,
  ClipboardList,
  PackageSearch,
  Radar,
  Search,
  ShoppingBasket,
} from 'lucide-react';
import {
  type StockGroupDto,
  type StockShortageDto,
} from '@eombor/contracts';
import { Button, Card, EmptyState, Input, Skeleton, cn, useToast } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { useNomenclatures } from '../../catalog/model/catalog.queries.js';
import { useStockSearch } from '../../stock/model/stock.queries.js';
import { StockProductCard } from '../../stock/ui/StockProductCard.js';
import { WarehouseSweep } from '../../stock/ui/WarehouseSweep.js';
import type { WarehouseStock } from '../../stock/model/stock-warehouses.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import { requisitionApi, type CreateRequisitionBody } from '../api/requisition.api.js';
import {
  demandMapOf,
  newIdempotencyKey,
  usePendingDemand,
} from '../model/requisition.queries.js';
import { requisitionErrorText } from '../model/requisition-error.js';
import { canSendFree, missingQtyOf, shortagesOfError } from '../model/shortage.js';
import {
  basketKey,
  readyGroups,
  readyLineCount,
  toRequisitionLines,
  type BasketLine,
} from '../model/basket.js';
import { backorderApi } from '../../backorder/api/backorder.api.js';
import { BackorderDialog } from '../../backorder/ui/BackorderDialog.js';
import { InsufficientStockDialog } from './InsufficientStockDialog.js';
import { AddLineDialog } from './AddLineDialog.js';
import { BasketDialog, type DocumentFields } from './BasketDialog.js';

const CREATE_PERMISSION = 'requisition.create';
const BACKORDER_PERMISSION = 'backorder.create';

/**
 * Supurgi eng kamida shuncha turadi.
 *
 * Mock rejimida javob 30 ms da keladi va animatsiya ko'z ilg'amay o'tib
 * ketardi — ekran "chaqnab" qo'ygandek bo'lardi. Qamrov kengayganini odam
 * PAYQASHI kerak, aks holda natijaning nega ko'payib qolgani tushunarsiz.
 */
const MIN_SWEEP_MS = 900;

/** Bugungi sana `<input type="date">` formatida (mahalliy vaqt bo'yicha). */
const today = (): string => {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export interface RequisitionCreatePageProps {
  /**
   * Yaratilgan talabnomalar — bittadan ko'p bo'lishi MUMKIN.
   *
   * Savat ombor bo'yicha bo'linadi va har bo'lak alohida hujjat bo'ladi,
   * shuning uchun chaqiruv bitta identifikator emas, ro'yxat oladi:
   * qayerga o'tishni marshrut hal qiladi.
   */
  onCreated?: (requisitionIds: readonly string[]) => void;
  onCancel?: () => void;
}

/**
 * Talabnoma yaratish — «AVVAL TOP, OMBOR O'ZI TOPILADI».
 *
 * Ilgari ekran birinchi savol qilib omborni so'rardi va qidiruv shu ombor
 * ichida ishlardi. Bu buyurtmachidan JAVOBINI BILMAYDIGAN narsani so'rash
 * edi: mahsulot qaysi omborda turganini u qayerdan bilsin? Kerakli narsa
 * qo'shni omborda bo'lsa, ekran uni umuman ko'rsatmasdi va odam "yo'q ekan"
 * degan yolg'on xulosaga kelardi.
 *
 * Endi ombor tanlov emas, NATIJA: qidiruv hamma ombor bo'ylab yuradi,
 * kartochka mahsulot qaysi omborlarda borligini aytadi va odam o'shalardan
 * birini bosadi. Savat esa ombor bo'yicha bo'linib, har bo'lakdan alohida
 * talabnoma tug'iladi — domen qoidasi buzilmaydi, lekin u ekranda KO'RINADI.
 *
 * SAHIFANING O'ZI faqat bitta ish qiladi — qidiradi. Miqdor so'rash, savatni
 * ko'zdan kechirish va yuborish oynalarda: ular ketma-ket bajariladigan
 * qadamlar va bir vaqtda ikkitasi kerak emas. Yon ustunda turganda savat
 * yo bo'sh qolib ekranni yeb yotardi, yo qidiruvning yarmini o'zi olardi.
 *
 * Qoldiqda YO'Q mahsulot bu ekranda umuman chiqmaydi (`onlyAvailable`):
 * omborda bo'lmagan narsa uchun ariza — boshqa hujjat va boshqa yo'l.
 */
export const RequisitionCreatePage = ({ onCreated, onCancel }: RequisitionCreatePageProps) => {
  const { can, canAnywhere } = usePermission();
  const toast = useToast();

  const [warehouseId, setWarehouseId] = useState('');
  const [q, setQ] = useState('');
  const [basket, setBasket] = useState<BasketLine[]>([]);
  const [fields, setFields] = useState<DocumentFields>({
    purpose: '',
    /*
     * «Через кого» — trebovaniya blankidagi chiziq.
     *
     * Savol AYNAN shu oqimda so'raladi va bu ataylab: kimni yuborishini
     * ariza yozayotgan odam biladi. Skladchidan yoki buxgalterdan so'rash
     * javobni bilmaydigan odamdan so'rash bo'lardi.
     */
    throughPerson: '',
    neededBy: today(),
    note: '',
  });
  /** Miqdor so'ralayotgan satr — `null` bo'lsa oyna yopiq. */
  const [pending, setPending] = useState<{
    group: StockGroupDto;
    warehouse: WarehouseStock;
  } | null>(null);
  const [basketOpen, setBasketOpen] = useState(false);
  const [backorderOpen, setBackorderOpen] = useState(false);
  /** Qamrov ataylab kengaytirildi — supurgi shuning uchun ko'rinadi. */
  const [sweeping, setSweeping] = useState(false);

  const warehouses = useWarehouses({ limit: 200 });
  const nomenclatures = useNomenclatures({ limit: 200 });

  /* Ombor ro'yxati RUXSAT bo'yicha filtrlanadi: talabnoma yoza olmaydigan
     omborni tanlab, xatoni faqat saqlashda ko'rish — bekorga sarflangan
     mehnat. `useMemo` YO'Q: `can` har chizishda yangi funksiya bo'ladi. */
  const allowedWarehouses = (warehouses.data?.items ?? []).filter((warehouse) =>
    can(CREATE_PERMISSION, {
      warehouseId: warehouse.id,
      mtuId: warehouse.mtuId,
      orgUnitId: warehouse.orgUnitId,
    }),
  );
  const allowedIds = new Set(allowedWarehouses.map((warehouse) => warehouse.id));

  const stock = useStockSearch({
    ...(q === '' ? {} : { q }),
    ...(warehouseId === '' ? {} : { warehouseId }),
    onlyAvailable: true,
    limit: 24,
  });

  /*
   * KUTILAYOTGAN TALAB — faqat EKRANDA ko'ringan mahsulotlar uchun.
   *
   * Butun ombor talabini tortib olish ko'rinmaydigan mingta qatorni behuda
   * olib kelardi. Ombor filtri berilmaydi: kartochka bir necha omborni
   * ko'rsatadi va har biriga o'z soni kerak.
   */
  const shownIds = (stock.data?.items ?? []).map((group) => group.nomenclatureId);
  const demand = usePendingDemand(shownIds);
  const demandMap = useMemo(() => demandMapOf(demand.data ?? []), [demand.data]);

  const thresholds = useMemo(
    () => new Map((nomenclatures.data?.items ?? []).map((item) => [item.id, item.minStock])),
    [nomenclatures.data],
  );

  /* Supurgi eng kamida `MIN_SWEEP_MS` turadi — javob undan tez kelsa ham. */
  useEffect(() => {
    if (!sweeping) return undefined;
    const timer = setTimeout(() => setSweeping(false), MIN_SWEEP_MS);
    return () => clearTimeout(timer);
  }, [sweeping]);

  /**
   * Yuborilgan hujjatlar — ombor bo'yicha.
   *
   * Yarim muvaffaqiyat haqiqiy hol: birinchi talabnoma yozilib, ikkinchisida
   * tarmoq uzilishi mumkin. Qayta urinishda BAJARILGANI o'tkazib yuboriladi,
   * aks holda birinchi hujjat ikkinchi marta yuborilardi.
   */
  const doneRef = useRef(new Map<string, { id: string; docNo: string; submitted: boolean }>());
  /** Kalit ombor bo'yicha bir marta yasaladi — qayta urinishda AYNAN shu ketadi. */
  const keysRef = useRef(new Map<string, string>());

  /**
   * QOLDIQ YETMAGAN hujjat — oyna shu ustida ishlaydi.
   *
   * `useRef`, chunki qiymat `mutationFn` ichida, `onError` dan OLDIN
   * yoziladi va oraliqda qayta chizish kerak emas. Holatga esa faqat
   * server tafsiloti kelgandan keyin o'tadi.
   */
  const shortRef = useRef<{ requisitionId: string; warehouseId: string; docNo: string } | null>(
    null,
  );
  const [short, setShort] = useState<
    { requisitionId: string; warehouseId: string; docNo: string; shortages: StockShortageDto[] } | null
  >(null);

  const bodyOf = (
    lines: readonly BasketLine[],
    groupWarehouseId: string,
  ): CreateRequisitionBody => ({
    warehouseId: groupWarehouseId,
    purpose: fields.purpose.trim(),
    ...(fields.throughPerson.trim() === '' ? {} : { throughPerson: fields.throughPerson.trim() }),
    ...(fields.neededBy === ''
      ? {}
      : { neededBy: new Date(`${fields.neededBy}T00:00:00`).toISOString() }),
    ...(fields.note.trim() === '' ? {} : { note: fields.note.trim() }),
    lines: toRequisitionLines(lines),
  });

  /**
   * Saqlash — har ombor uchun bitta hujjat.
   *
   * "Yuborish" bosilganda talabnoma avval yaratiladi, keyin `DRAFT → SUBMITTED`
   * o'tishi bajariladi: holatni O'ZGARTIRISH yagona yo'ldan (`/transition`)
   * boradi va yaratish endpointi hech qachon tayyor talabnomani tug'dirmaydi.
   *
   * Ketma-ket, parallel emas: bir vaqtda yuborilgan hujjatlar xato bo'lganda
   * qaysi biri o'tib, qaysi biri o'tmaganini aytish qiyin bo'lardi.
   */
  const save = useMutation({
    mutationFn: async (submit: boolean) => {
      for (const group of readyGroups(basket)) {
        const before = doneRef.current.get(group.warehouseId);
        let id = before?.id;
        let docNo = before?.docNo;

        if (id === undefined || docNo === undefined) {
          const key = keysRef.current.get(group.warehouseId) ?? newIdempotencyKey();
          keysRef.current.set(group.warehouseId, key);
          const created = await requisitionApi.create(bodyOf(group.lines, group.warehouseId), key);
          id = created.id;
          docNo = created.docNo;
          doneRef.current.set(group.warehouseId, { id, docNo, submitted: false });
        }

        if (submit && !(doneRef.current.get(group.warehouseId)?.submitted ?? false)) {
          try {
            await requisitionApi.transition(id, { to: 'SUBMITTED' }, newIdempotencyKey());
          } catch (err) {
            /* QAYSI hujjat to'silganini eslab qolamiz: `onError` ga
               faqat xatoning o'zi yetadi, oyna esa hujjatni ham
               bilishi kerak — miqdorni o'sha hujjatda tuzatadi. */
            if (shortagesOfError(err) !== null) {
              shortRef.current = { requisitionId: id, warehouseId: group.warehouseId, docNo };
            }
            throw err;
          }
          doneRef.current.set(group.warehouseId, { id, docNo, submitted: true });
        }
      }
      return { docs: [...doneRef.current.values()], submitted: submit };
    },
    onSuccess: ({ docs, submitted }) => {
      const docNos = docs.map((doc) => doc.docNo).join(', ');
      toast.show(
        docs.length === 1
          ? t(submitted ? 'requisition.submitted' : 'requisition.created', { docNo: docNos })
          : t(submitted ? 'requisition.submittedMany' : 'requisition.createdMany', {
              count: docs.length,
              docNos,
            }),
        'success',
      );
      setBasketOpen(false);
      onCreated?.(docs.map((doc) => doc.id));
    },
    onError: (error) => {
      const found = shortagesOfError(error);
      if (found === null || shortRef.current === null) return;
      setShort({ ...shortRef.current, shortages: found });
    },
  });

  /**
   * QOLDIQ YETMAGANDA tanlangan yo'lni bajaradi.
   *
   * Uch qadam, shu tartibda:
   *   1. yetmagan mahsulotlarning satrlari o'chiriladi va o'rniga BOR
   *      miqdor bilan bittadan satr qo'yiladi (bir mahsulot bir necha
   *      satrda kelgan bo'lishi mumkin — server ham ularni yig'ib
   *      tekshirgan edi);
   *   2. so'ralgan bo'lsa, YETMAGAN qismga bildirgi yoziladi;
   *   3. hujjat yuboriladi.
   *
   * Bildirgi yuborishdan OLDIN yoziladi: yuborish yana to'silsa ham
   * (shu oraliqda yana kimdir ulgursa) ariza yo'qolmaydi.
   *
   * Hech qanday satr qolmasa yuborish O'TKAZIB YUBORILADI: bo'sh
   * hujjatni server `hasLines` sharti bilan baribir rad etardi va odam
   * ikkinchi xatoni ko'rardi. Qoralama joyida qoladi.
   */
  const resolveShort = useMutation({
    mutationFn: async (withBackorder: boolean) => {
      if (short === null) throw new Error('Tuzatiladigan hujjat yo\'q');

      const detail = await requisitionApi.byId(short.requisitionId);
      const shortOf = new Map(short.shortages.map((row) => [row.nomenclatureId, row]));

      for (const line of detail.lines) {
        if (!shortOf.has(line.nomenclatureId)) continue;
        await requisitionApi.removeLine(short.requisitionId, line.id);
      }
      for (const row of short.shortages) {
        if (row.free <= 0) continue;
        await requisitionApi.addLine(short.requisitionId, {
          nomenclatureId: row.nomenclatureId,
          requestedQty: row.free,
        });
      }

      let backordered = 0;
      if (withBackorder) {
        for (const row of short.shortages) {
          const missing = missingQtyOf(row);
          if (missing <= 0) continue;
          await backorderApi.create({
            warehouseId: short.warehouseId,
            nomenclatureId: row.nomenclatureId,
            freeText: null,
            requestedQty: missing,
            purpose: fields.purpose.trim(),
            neededBy:
              fields.neededBy === '' ? null : new Date(`${fields.neededBy}T00:00:00`),
            note: null,
          });
          backordered += 1;
        }
      }

      const kept =
        detail.lines.filter((line) => !shortOf.has(line.nomenclatureId)).length +
        short.shortages.filter((row) => row.free > 0).length;

      if (kept > 0) {
        await requisitionApi.transition(
          short.requisitionId,
          { to: 'SUBMITTED' },
          newIdempotencyKey(),
        );
        doneRef.current.set(short.warehouseId, {
          id: short.requisitionId,
          docNo: short.docNo,
          submitted: true,
        });
      }

      return { backordered, sent: kept > 0, docNo: short.docNo };
    },
    onSuccess: ({ backordered, sent, docNo }) => {
      if (sent) toast.show(t('requisition.shortTrimmed', { docNo }), 'success');
      if (backordered > 0) {
        toast.show(t('requisition.shortBackordered', { count: backordered }), 'success');
      }
      setShort(null);
      shortRef.current = null;
      /* Savatda boshqa ombor ham bo'lishi mumkin — qolganini davom
         ettiramiz. Bajarilgani `doneRef` orqali o'tkazib yuboriladi. */
      save.mutate(true);
    },
  });

  const addLine = (line: BasketLine): void => {
    setBasket((prev) =>
      prev.some((old) => basketKey(old) === basketKey(line))
        ? prev.map((old) => (basketKey(old) === basketKey(line) ? line : old))
        : [...prev, line],
    );
    setPending(null);
  };

  const patchLine = (key: string, patch: Partial<BasketLine>): void => {
    setBasket((prev) =>
      prev.map((line) => (basketKey(line) === key ? { ...line, ...patch } : line)),
    );
  };

  const removeLine = (key: string): void => {
    setBasket((prev) => {
      const next = prev.filter((line) => basketKey(line) !== key);
      /* Oxirgi satr olib tashlansa oyna o'zi yopiladi: bo'sh savatni
         ko'rsatib turish odamdan bekorga bitta bosish so'rardi. */
      if (next.length === 0) setBasketOpen(false);
      return next;
    });
  };

  /** Qamrovni kengaytirish — ombor filtri olib tashlanadi. */
  const searchEverywhere = (): void => {
    setWarehouseId('');
    setSweeping(true);
  };

  if (!canAnywhere(CREATE_PERMISSION)) {
    return (
      <Card>
        <EmptyState
          title={t('requisition.createForbidden')}
          description={t('requisition.createForbiddenHint')}
        />
      </Card>
    );
  }

  const items = stock.data?.items ?? [];

  /* Bildirgi tugmasi BITTA — uch joyda ko'rinadi. Har biriga alohida
     yozilsa, ruxsat tekshiruvi bir kun ulardan birida unutilardi. */
  const backorderButton = canAnywhere(BACKORDER_PERMISSION) ? (
    <Button variant="secondary" onClick={() => setBackorderOpen(true)}>
      <ClipboardList size={15} aria-hidden />
      {t('backorder.new')}
    </Button>
  ) : null;
  /* Kartochka "shu mahsulot qaysi omborlarda savatda?" degan savolga javob
     beradi — kalit ombor va nomenklaturaning JUFTLIGI. */
  const orderedOf = (nomenclatureId: string): ReadonlySet<string> =>
    new Set(
      basket
        .filter((line) => line.nomenclatureId === nomenclatureId)
        .map((line) => line.warehouseId),
    );

  const ready = readyLineCount(basket);
  const documents = readyGroups(basket).length;
  const canSave =
    fields.purpose.trim().length >= 3 &&
    ready > 0 &&
    (fields.throughPerson === '' || fields.throughPerson.trim().length >= 2);

  /* Qadam — savatning holatidan hisoblanadi, alohida saqlanmaydi: ikki
     manba bo'lsa, ular ertami-kechmi bir-biriga zid bo'lib qolardi. */
  const step = basket.length === 0 ? 0 : canSave ? 2 : 1;
  const filterName = allowedWarehouses.find((w) => w.id === warehouseId)?.name ?? '';

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold tracking-tight text-text">
            {t('requisition.createTitle')}
          </h1>
          <p className="max-w-prose text-sm text-text-muted">{t('requisition.createSubtitle')}</p>
        </div>

        {onCancel === undefined ? null : (
          <Button variant="ghost" onClick={onCancel}>
            {t('requisition.cancel')}
          </Button>
        )}
      </header>

      <CreateSteps step={step} />

      <Card>
        <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          {/* Qidiruv OCHIQ turadi: ilgari u ombor tanlangunicha o'chirilgan
              bo'lardi va ekran ishlamayotgandek ko'rinardi. */}
          <Input
            label={t('requisition.searchAnyLabel')}
            placeholder={t('requisition.searchAnyPlaceholder')}
            prefix={<Search size={16} aria-hidden />}
            autoComplete="off"
            value={q}
            onChange={(event) => setQ(event.target.value)}
          />
          <Select
            label={t('requisition.fieldWarehouse')}
            placeholder={t('requisition.filterAllWarehouses')}
            value={warehouseId}
            onChange={(event) => setWarehouseId(event.target.value)}
            options={allowedWarehouses.map((warehouse) => ({
              value: warehouse.id,
              label: `${warehouse.code} — ${warehouse.name}`,
            }))}
            hint={
              allowedWarehouses.length === 0
                ? t('requisition.noWarehouse')
                : t('requisition.warehouseFilterHint')
            }
          />
        </div>
      </Card>

      {sweeping ? (
        <WarehouseSweep
          query={q}
          warehouseNames={allowedWarehouses.map((warehouse) => warehouse.name)}
        />
      ) : stock.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-56 w-full" />
        </div>
      ) : stock.isError ? (
        <Card>
          <EmptyState
            title={t('requisition.searchError')}
            description={requisitionErrorText(stock.error, '')}
            action={
              <Button variant="secondary" onClick={() => void stock.refetch()}>
                {t('requisition.retry')}
              </Button>
            }
          />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          {/* Ombor filtri qo'yilgan bo'lsa — bu boshi berk ko'cha EMAS:
              mahsulot qo'shni omborda turgan bo'lishi mumkin va ekran buni
              aytishi kerak. Filtrsiz bo'sh natija esa haqiqatan "qoldiqda
              yo'q" degani. */}
          {/* Boshi berk ko'chaga CHIQISH yo'li: bu ekran faqat qoldiqda
              BOR narsani ko'rsatadi (`onlyAvailable`), ya'ni bo'sh natija
              ko'pincha «bu yerda yo'q» degani. Ilgari odam shu yerda
              to'xtab, skladchini telefon qilib qidirardi — so'rov esa hech
              qayerda qolmasdi va kim nimani kutayotgani ko'rinmasdi. */}
          {warehouseId === '' ? (
            <EmptyState
              icon={<PackageSearch size={20} aria-hidden />}
              title={t('requisition.stockEmpty')}
              description={t('requisition.stockEmptyHint')}
              {...(backorderButton === null ? {} : { action: backorderButton })}
            />
          ) : (
            <EmptyState
              icon={<PackageSearch size={20} aria-hidden />}
              title={t('requisition.notInWarehouse', { warehouse: filterName })}
              description={t('requisition.notInWarehouseHint')}
              action={
                <div className="flex flex-wrap justify-center gap-2">
                  <Button onClick={searchEverywhere}>
                    <Radar size={15} aria-hidden />
                    {t('requisition.searchEverywhere')}
                  </Button>
                  {backorderButton}
                </div>
              }
            />
          )}
        </Card>
      ) : (
        <>
          <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((group, index) => (
              <StockProductCard
                key={group.nomenclatureId}
                group={group}
                index={index}
                minStock={thresholds.get(group.nomenclatureId) ?? null}
                orderedWarehouseIds={orderedOf(group.nomenclatureId)}
                allowedWarehouseIds={allowedIds}
                pendingOf={(id) => demandMap.get(`${id}:${group.nomenclatureId}`) ?? 0}
                onOrder={(g, warehouse) => setPending({ group: g, warehouse })}
              />
            ))}
          </ul>

          {/* Natija BOR, lekin bitta ombor ichida. Kerakligi topilmagan
              bo'lishi mumkin — qamrovni kengaytirish yo'li ochiq qoladi. */}
          {warehouseId === '' ? null : (
            <Button variant="secondary" onClick={searchEverywhere}>
              <Radar size={15} aria-hidden />
              {t('requisition.searchEverywhere')}
            </Button>
          )}

          {/* Natija BOR — lekin kerakli mahsulot orasida bo'lmasligi mumkin.
              «kabel» deb qidirilganda boshqa kabellar chiqadi va tugma
              faqat butunlay bo'sh natijada tursa, odam aynan o'sha paytda
              uni ko'rmay qolardi. Ro'yxat oxirida turadi: odam kartochkalarni
              ko'zdan kechirib, topmagan joyida unga duch keladi. */}
          {backorderButton === null ? null : (
            <Card className="flex flex-wrap items-center gap-3">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="text-sm font-medium text-text">{t('backorder.notFound')}</p>
                <p className="text-sm text-text-muted">{t('backorder.notFoundHint')}</p>
              </div>
              {backorderButton}
            </Card>
          )}
        </>
      )}

      {basket.length === 0 ? null : <BasketBar
        lines={basket.length}
        documents={documents}
        onOpen={() => setBasketOpen(true)}
      />}

      <AddLineDialog target={pending} onClose={() => setPending(null)} onAdd={addLine} />

      <BasketDialog
        open={basketOpen}
        onClose={() => setBasketOpen(false)}
        lines={basket}
        onPatch={patchLine}
        onRemove={removeLine}
        fields={fields}
        onFields={(patch) => setFields((prev) => ({ ...prev, ...patch }))}
        onSave={(submit) => save.mutate(submit)}
        savingDraft={save.isPending && save.variables === false}
        savingSubmit={save.isPending && save.variables === true}
        /* Qoldiq yetmagani XATO EMAS, tanlov — uni savatda qizil matn
           bilan takrorlash odamni ikki joyga qaratardi. Oyna ochiq
           bo'lsa savat jim turadi. */
        error={
          save.isError && short === null
            ? requisitionErrorText(save.error, t('requisition.saveFailed'))
            : null
        }
      />

      {short === null ? null : (
        <InsufficientStockDialog
          shortages={short.shortages}
          /* Talabnomada yetarli satrlar ham bo'lishi mumkin: bitta
             mahsulot butunlay tugagan bo'lsa ham, qolganlari
             yuborilishi kerak. Javob SAVATdan hisoblanadi — hujjatning
             o'zi bilan bir xil satrlardan tuzilgan. */
          canTrim={canSendFree(
            short.shortages,
            basket.some(
              (line) =>
                line.warehouseId === short.warehouseId &&
                !short.shortages.some((row) => row.nomenclatureId === line.nomenclatureId),
            ),
          )}
          canBackorder={canAnywhere(BACKORDER_PERMISSION)}
          busy={resolveShort.isPending}
          {...(resolveShort.isError
            ? { error: requisitionErrorText(resolveShort.error, t('requisition.saveFailed')) }
            : {})}
          onTrim={() => resolveShort.mutate(false)}
          onTrimAndBackorder={() => resolveShort.mutate(true)}
          onClose={() => {
            setShort(null);
            shortRef.current = null;
          }}
        />
      )}

      {/* Qidiruvda terilgan matn oynaga O'TADI: odam bir marta yozgan
          narsani ikkinchi marta yozmasligi kerak. */}
      {backorderOpen ? (
        <BackorderDialog
          open
          onClose={() => setBackorderOpen(false)}
          initialText={q.trim()}
          {...(warehouseId === '' ? {} : { defaultWarehouseId: warehouseId })}
        />
      ) : null}
    </div>
  );
};

/**
 * SAVAT QATORI — ekranning pastida yopishib turadi.
 *
 * Savat oynaga ko'chgach, uni ochadigan narsa doim ko'rinib turishi kerak:
 * sarlavhadagi kichkina tugma sahifa pastga siljiganda ekrandan chiqib
 * ketardi va odam qo'shgan satrlari qayerga tushayotganini yo'qotardi.
 *
 * Qator faqat savat BO'SH EMASLIGIDA chiziladi — bo'sh qator ekranning
 * pastini bekorga band qilardi.
 */
const BasketBar = ({
  lines,
  documents,
  onOpen,
}: {
  lines: number;
  documents: number;
  onOpen: () => void;
}) => (
  <div
    className={cn(
      'sticky z-[var(--z-sticky-bar)] mt-2',
      /* Telefonda panel pastki navigatsiya qatorining USTIDA to'xtaydi.
         `bottom-4` bo'lsa u qatorning ostiga tushib, butunlay ko'rinmay
         qolardi — qator karkas qatlamida va sahifadagi paneldan baland. */
      'bottom-[calc(var(--size-tabbar)_+_env(safe-area-inset-bottom)_+_0.5rem)] lg:bottom-4',
    )}
  >
    <div
      className={cn(
        'motion-slide-in flex flex-wrap items-center gap-3 rounded-[var(--radius-lg)]',
        'border border-accent/25 bg-surface px-4 py-3 shadow-[var(--shadow-3)]',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-9 shrink-0 items-center justify-center',
          'rounded-[var(--radius-md)] bg-accent-soft text-accent',
        )}
      >
        <ShoppingBasket size={17} />
      </span>

      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-semibold text-text">
          {t('requisition.basketCount', { count: lines })}
        </span>
        {/* Nechta hujjat tug'ilishi — savatni OCHMASDAN oldin. Bo'linish
            eng kutilmagan xabar va uni oxirgi qadamda ko'rish kech. */}
        {documents > 1 ? (
          <span className="text-xs text-text-muted">
            {t('requisition.splitNotice', { count: documents })}
          </span>
        ) : null}
      </span>

      <Button className="ml-auto" onClick={onOpen}>
        {t('requisition.openBasket')}
        <ArrowRight size={14} aria-hidden />
      </Button>
    </div>
  </div>
);

/**
 * Uch qadam — sahifaning xaritasi.
 *
 * Savat va yuborish endi oynalarda va ekranda ko'rinmaydi. Shunda esa
 * "keyin nima bo'ladi?" degan savol javobsiz qolardi — bu qator aynan shu
 * javobni beradi.
 */
const CreateSteps = ({ step }: { step: number }) => {
  const titles = [
    t('requisition.stepSearch'),
    t('requisition.stepBasket'),
    t('requisition.stepSend'),
  ];

  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-2">
      {titles.map((title, index) => {
        const done = index < step;
        const current = index === step;
        return (
          <li key={title} className="flex items-center gap-2">
            <span
              className={cn(
                'flex items-center gap-2 rounded-[var(--radius-pill)] border px-3 py-1.5',
                'text-xs font-medium transition-colors duration-[var(--motion-base)]',
                done
                  ? 'border-accent/30 bg-accent-soft text-accent'
                  : current
                    ? 'border-warn/30 bg-warn-soft text-warn'
                    : 'border-border bg-surface-2 text-text-faint',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'flex size-4 items-center justify-center rounded-full text-[0.625rem]',
                  done
                    ? 'bg-accent text-[var(--color-accent-contrast)]'
                    : current
                      ? 'bg-warn text-[var(--color-warn-contrast)] motion-pulse-soft [--pulse-color:var(--color-warn)]'
                      : 'bg-surface text-text-faint',
                )}
              >
                {done ? <Check size={10} /> : index + 1}
              </span>
              {title}
            </span>
            {index === titles.length - 1 ? null : (
              <span aria-hidden className="h-px w-4 bg-border" />
            )}
          </li>
        );
      })}
    </ol>
  );
};
